import Cookies from 'js-cookie'

import {
  AD_SERVER_METADATA_DATA_ATTR,
  AD_SERVER_TRACKING_URLS_COOKIE_PREFIX,
} from 'shared/consts/adServer'
import {
  EXPERIMENT_VARIATION,
  type ExperimentName,
} from 'shared/experiments/consts'
import store, { subscribe } from 'shared/store'
import { INIT_CROSSSELL } from 'shared/store/ducks/events'
import {
  CAMPAIGN_EXPERIMENT_MAP,
  type ClientCookieOptions,
  extractKevelIdsAndClickUrlFromMetadataContent,
  getTargetingKeywordsFromSearchParams,
  isAdServerEnabled,
} from 'shared/utils/adServerUtils'
import { isEmpty } from 'shared/utils/objectUtils'
import type { AdServerMetadataCookieWithPzn } from 'types/adServerTypes'
import { type Experiment } from 'types/shopConfig'
import {
  AD_BANNER_CAMPAIGN_NAME_DATA_ATTR,
  AD_BANNER_PARTNER_CODE_SEARCH_PARAM,
  AD_BANNER_TAGS_DATA_ATTR,
  AD_BANNER_TRIAD_CAMPAIGN_NAME,
} from 'views/consts'
import {
  handleCloseAdBanner,
  initAdServer,
  pushSetupPreloadedSlotsCommand,
} from 'views/utils/adServerUtils'

const CAMPAIGN_MAX_PAGE_VISITS = 5
const CAMPAIGN_MAX_AGE_DAYS = 1

const setupCrossSellAds = (slider: HTMLElement) => {
  let moreLink: Element | null

  const sponsoredItems: NodeListOf<HTMLElement> | undefined =
    slider?.querySelectorAll(`[${AD_SERVER_METADATA_DATA_ATTR}]`)

  if (slider && sponsoredItems?.length) {
    const wid: string | undefined | null =
      slider.getAttribute('data-crosssell-id')
    moreLink = slider.querySelector(
      '[data-clientside-hook~="adServerSliderMoreLink"]'
    )

    const cookieValue: AdServerMetadataCookieWithPzn = Object.fromEntries(
      [...sponsoredItems]
        .map(slot => ({
          content: extractKevelIdsAndClickUrlFromMetadataContent(
            JSON.parse(slot?.getAttribute(AD_SERVER_METADATA_DATA_ATTR) || '{}')
          ),
          pzn: slot?.getAttribute('data-product-id'),
        }))
        .map(meta => [meta.pzn, meta.content])
    )

    const hasCookie = !isEmpty(cookieValue)

    if (hasCookie && moreLink && wid) {
      const {
        publicConfig: { mountPoints },
      } = store.getPublicRuntimeConfig()
      const cookieOptions: ClientCookieOptions = {
        // 30 mins
        expires: 30 / 1440,
        path: mountPoints.SEARCH as string,
        httpOnly: false,
      }

      moreLink.addEventListener('click', () => {
        if (hasCookie) {
          Cookies.withConverter<never>({
            write: (value: string) => value,
          }).set(
            `${AD_SERVER_TRACKING_URLS_COOKIE_PREFIX}${wid}`,
            JSON.stringify(cookieValue),
            cookieOptions
          )
        }
      })
    }

    pushSetupPreloadedSlotsCommand(slider)
  }
}

const updateCampaignCookie = (campaignName: string, campaignCookie: string) => {
  const { pageVisitsCount, timeStamp, experimentVariant } =
    JSON.parse(campaignCookie)
  const newPageVisitsCount = pageVisitsCount + 1

  if (
    experimentVariant === EXPERIMENT_VARIATION.V2 &&
    newPageVisitsCount >= CAMPAIGN_MAX_PAGE_VISITS
  ) {
    Cookies.remove(campaignName)

    return
  }

  const newCookieValue = {
    pageVisitsCount: newPageVisitsCount,
    timeStamp,
    experimentVariant,
  }

  Cookies.set(campaignName, JSON.stringify(newCookieValue), {
    expires: new Date(timeStamp),
  })
}

const isCampaignEnabledBySearchParam = (
  campaignName: string,
  experiments: Experiment[]
) => {
  const experimentName: ExperimentName = CAMPAIGN_EXPERIMENT_MAP?.[campaignName]

  if (!experimentName) {
    return false
  }

  const experiment = experiments?.find(({ name }) => name === experimentName)
  const isExperimentEnabled = Boolean(experiment?.isEnabled)
  const experimentVariant = experiment?.variant
  const pageUrl = new URL(window.location.href)
  const partnerCode =
    pageUrl.searchParams.get(AD_BANNER_PARTNER_CODE_SEARCH_PARAM) || ''

  if (
    partnerCode === campaignName &&
    isExperimentEnabled &&
    (experimentVariant === EXPERIMENT_VARIATION.V1 ||
      experimentVariant === EXPERIMENT_VARIATION.V2)
  ) {
    const expires = new Date()
    expires.setDate(expires.getDate() + CAMPAIGN_MAX_AGE_DAYS)

    const cookieValue = {
      pageVisitsCount: 1,
      timeStamp: expires.getTime(),
      experimentVariant,
    }

    Cookies.set(campaignName, JSON.stringify(cookieValue), { expires })

    return true
  }

  return false
}

const isCampaignEnabled = (campaignName: string, experiments: Experiment[]) => {
  const campaignCookie = Cookies.get(campaignName)

  try {
    if (campaignCookie) {
      updateCampaignCookie(campaignName, campaignCookie)

      return true
    } else {
      return isCampaignEnabledBySearchParam(campaignName, experiments)
    }
  } catch (_) {
    return false
  }
}

const handleCampaign = (campaignName: string, experiments: Experiment[]) => {
  if (isCampaignEnabled(campaignName, experiments)) {
    ;[
      ...document.querySelectorAll(
        `[${AD_BANNER_CAMPAIGN_NAME_DATA_ATTR}="${campaignName}"]`
      ),
    ].forEach(slot => {
      slot.setAttribute(AD_BANNER_TAGS_DATA_ATTR, campaignName)
    })
  }
}

export default () => {
  const {
    pageProperties: { experiments },
  } = store.getPublicRuntimeConfig() || {}

  if (isAdServerEnabled(experiments)) {
    const keywords = window._targetingKeywords || []

    window._targetingKeywords = [
      ...keywords,
      ...getTargetingKeywordsFromSearchParams(window.location.search),
    ]

    subscribe.after(INIT_CROSSSELL, payload => {
      // @ts-ignore
      const { selector } = payload

      const slider = document.querySelector(selector)

      setupCrossSellAds(slider)
    })

    handleCampaign(AD_BANNER_TRIAD_CAMPAIGN_NAME, experiments)

    initAdServer()

    handleCloseAdBanner()
  }
}
